import { MutableRefObject, useRef, useState } from "react";

import {
  useMultisigEnrollmentSubmitKey,
  useMultisigEnrollmentVerifyKey,
} from "@/apiQueries/multisigEnrollment";

import { CoSignerKeyResult } from "../components/CoSignerKeyStep";

/**
 * Shared hooks
 */
export const useEnrollmentKeySignFlow = () => {
  const submitKeyMutation = useMultisigEnrollmentSubmitKey();
  const verifyKeyMutation = useMultisigEnrollmentVerifyKey();

  const [keyResult, setKeyResult] = useState<CoSignerKeyResult | null>(null);
  const [challengeXdr, setChallengeXdr] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  const [signRequestPending, setSignRequestPending] = useState<boolean>(false);

  const disconnectWalletRef: MutableRefObject<(() => Promise<void>) | null> = useRef(null);
  const signRef: MutableRefObject<((xdr: string) => Promise<string>) | null> = useRef(null);

  const hasConnectedKey = !!keyResult;

  const canProceed =
    !!keyResult && !!challengeXdr && !submitKeyMutation.isPending && !submitKeyMutation.isError;

  const isBusy = signRequestPending || verifyKeyMutation.isPending;

  const handleKeyConnected = async (result: CoSignerKeyResult) => {
    setKeyResult(result);
    setChallengeXdr(null);
    submitKeyMutation.reset();
    try {
      const challenge = await submitKeyMutation.mutateAsync({ publicKey: result.publicKey });
      setChallengeXdr(challenge.challenge_xdr);
    } catch {
      // Error banner surfaces the mapper output; disconnect + rescan to retry.
    }
  };

  const handleKeyDisconnected = () => {
    setKeyResult(null);
    setChallengeXdr(null);
    setSignError(null);
    submitKeyMutation.reset();
    verifyKeyMutation.reset();
  };

  const signAndVerify = async (): Promise<boolean> => {
    if (!challengeXdr) return false;
    if (!signRef.current) {
      setSignError("VIA Wallet is not connected. Disconnect and pair again.");
      return false;
    }
    let effectiveChallenge = challengeXdr;
    if (verifyKeyMutation.isError && keyResult) {
      try {
        const fresh = await submitKeyMutation.mutateAsync({ publicKey: keyResult.publicKey });
        effectiveChallenge = fresh.challenge_xdr;
        setChallengeXdr(effectiveChallenge);
      } catch {
        return false;
      }
    }
    setSignError(null);
    verifyKeyMutation.reset();
    setSignRequestPending(true);
    let signedXdr: string;
    try {
      signedXdr = await signRef.current(effectiveChallenge);
    } catch (err) {
      setSignError(
        err instanceof Error && err.message
          ? err.message
          : "VIA Wallet couldn't sign the request. Try again.",
      );
      return false;
    } finally {
      setSignRequestPending(false);
    }
    try {
      await verifyKeyMutation.mutateAsync({ signedChallengeXdr: signedXdr });
    } catch {
      return false;
    }
    return true;
  };

  return {
    // State
    keyResult,
    challengeXdr,
    hasConnectedKey,
    signError,
    signRequestPending,

    // Refs (wired into CoSignerKeyStep)
    disconnectWalletRef,
    signRef,

    // Mutations (view surfaces them via banners)
    submitKeyMutation,
    verifyKeyMutation,

    // Handlers (wired into CoSignerKeyStep and Next / Confirm buttons)
    handleKeyConnected,
    handleKeyDisconnected,
    signAndVerify,

    // Derived
    canProceed,
    isBusy,
  };
};
